import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import Search from '@mui/icons-material/Search';
import UploadFile from '@mui/icons-material/UploadFile';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import api from '../../services/api';
import { useMediaObjectUrl } from '../../hooks/useMediaObjectUrl';

export type MediaAccept = 'image' | 'video' | 'audio' | 'document' | 'any';

export interface PickedMedia {
    id: number;
    uuid: string;
    originalFilename: string;
    mimeType?: string;
    fileType?: string;
    size?: number;
}

interface MediaPickerDialogProps {
    open: boolean;
    onClose: () => void;
    onSelect: (media: PickedMedia) => void;
    accept?: MediaAccept;
    selectedUuid?: string;
    title?: string;
}

const ACCEPT_MIME: Record<MediaAccept, string> = {
    image: 'image/*',
    video: 'video/*',
    audio: 'audio/*',
    document: '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt',
    any: '*/*',
};

const Thumb: React.FC<{ media: PickedMedia }> = ({ media }) => {
    const isImage = (media.mimeType ?? '').startsWith('image/') || media.fileType === 'image';
    const url = useMediaObjectUrl(isImage ? media.uuid : null);
    if (isImage && url) {
        return <img src={url} alt={media.originalFilename} className="oa-media-thumb" loading="lazy" />;
    }
    return (
        <Box className="oa-media-thumb oa-media-thumb-icon">
            <InsertDriveFileOutlined />
        </Box>
    );
};

/**
 * Shared picker for the media library (plan §6.7): search, pick, or upload a new
 * file. Replaces the inline dialogs of the classic editors for new code.
 */
export const MediaPickerDialog: React.FC<MediaPickerDialogProps> = ({ open, onClose, onSelect, accept = 'any', selectedUuid, title }) => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [query, setQuery] = useState('');
    const [files, setFiles] = useState<PickedMedia[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const filter: Record<string, unknown> = {};
            if (accept !== 'any') filter.fileType = accept;
            if (query.trim()) filter.originalFilename = query.trim();
            const { data } = await dataProvider.getList('media_files', {
                pagination: { page: 1, perPage: 60 },
                sort: { field: 'id', order: 'DESC' },
                filter,
            });
            setFiles(
                (data as Array<Record<string, unknown>>).map((f) => ({
                    id: Number(f.id),
                    uuid: String(f.uuid ?? ''),
                    originalFilename: String(f.originalFilename ?? ''),
                    mimeType: f.mimeType as string | undefined,
                    fileType: f.fileType as string | undefined,
                    size: f.size as number | undefined,
                })),
            );
        } catch {
            notify('mediaPicker.loadError', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [accept, query, dataProvider, notify]);

    useEffect(() => {
        if (!open) return;
        const t = setTimeout(load, query ? 250 : 0);
        return () => clearTimeout(t);
    }, [open, load, query]);

    const upload = async (file: File) => {
        setUploading(true);
        try {
            const form = new FormData();
            form.append('file', file);
            const { data: response } = await api.post('/media/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
            // #520: the upload endpoint wraps the record as { success, file: {...} } —
            // reading the uuid off the envelope produced src="/api/media/undefined".
            const data = response?.file ?? response;
            if (!data?.uuid) throw new Error('upload response without uuid');
            const media: PickedMedia = {
                id: Number(data.id),
                uuid: String(data.uuid),
                originalFilename: String(data.originalFilename ?? data.filename ?? file.name),
                mimeType: data.mimeType ?? file.type,
                fileType: data.fileType,
                size: data.size,
            };
            notify('mediaPicker.uploadSuccess', { type: 'success' });
            onSelect(media);
            onClose();
        } catch {
            notify('mediaPicker.uploadError', { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{title ?? translate(`mediaPicker.title.${accept}`)}</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        fullWidth
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={translate('mediaPicker.search')}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                        sx={{ flex: 1, minWidth: 200 }}
                    />
                    <input ref={fileInput} type="file" accept={ACCEPT_MIME[accept]} hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
                    <Button variant="outlined" startIcon={uploading ? <CircularProgress size={16} /> : <UploadFile />} disabled={uploading} onClick={() => fileInput.current?.click()}>
                        {translate('mediaPicker.upload')}
                    </Button>
                </Box>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                ) : files.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>{translate('mediaPicker.empty')}</Typography>
                ) : (
                    <Box className="oa-media-grid">
                        {files.map((f) => (
                            <button
                                key={f.id}
                                type="button"
                                className={`oa-media-card${f.uuid === selectedUuid ? ' is-selected' : ''}`}
                                onClick={() => {
                                    onSelect(f);
                                    onClose();
                                }}
                                title={f.originalFilename}
                            >
                                <Thumb media={f} />
                                <span className="oa-media-name">{f.originalFilename}</span>
                            </button>
                        ))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{translate('ra.action.cancel')}</Button>
            </DialogActions>
            <style>{`
                .oa-media-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 12px; }
                .oa-media-card { display: flex; flex-direction: column; gap: 6px; padding: 6px; border: 2px solid transparent; border-radius: var(--oa-radius-md, 8px); background: #f4f4f4; cursor: pointer; font-family: inherit; text-align: left; }
                .oa-media-card:hover { border-color: var(--oa-divider); }
                .oa-media-card.is-selected { border-color: var(--oa-primary); }
                .oa-media-thumb { width: 100%; aspect-ratio: 4 / 3; object-fit: cover; border-radius: var(--oa-radius-sm, 6px); background: #e1e2e4; }
                .oa-media-thumb-icon { display: flex; align-items: center; justify-content: center; color: var(--oa-text-2); }
                .oa-media-name { font-size: .78rem; color: var(--oa-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            `}</style>
        </Dialog>
    );
};

export default MediaPickerDialog;
